import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  FileText, 
  Target,
  ArrowLeft,
  ArrowRight,
  BarChart3,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  MapPin,
  Building,
  Briefcase,
  Info,
  AlertTriangle,
  XCircle,
  Settings
} from 'lucide-react';
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';

import { analyzeJobMatch, getOptimizationGuidance } from '@soVendor/utils/jobMatchingAlgorithm';
import { detectRoleMismatch, RoleMismatchResult } from '@soVendor/utils/roleMismatchDetector';
import { ResumeData, JobData, BaselineScores } from '../shared/types';
import {
  STEP_WRAPPER_CLASS,
  STEP_MOTION_CLASS,
  STEP_CARD_COMPACT_CLASS,
  STEP_SCROLL_AREA_CLASS,
  STEP_NAV_CLASS,
  STEP_INNER_HEADER_CLASS,
  STEP_HEADER_ICON_CLASS,
} from '../shared/stepLayout';

interface BaselineAnalysisStepProps {
  onNext: () => void;
  onBack: () => void;
  resumeData: ResumeData;
  jobData?: JobData;
  baselineScores: BaselineScores;
  onOptimize: (preferences: any) => void;
  isLoading: boolean;
  error: string | null;
}

// ─── Animated Score Graph ────────────────────────────────────────────────────
interface AnimatedScoreGraphProps {
  baseline: number;
  expectedMin: number;
  expectedMax: number;
  /** Same ATS pillars as the left column — avoids conflicting “Skills/Job fit” % from a different model */
  formatting: number;
  keywords: number;
  structure: number;
  pillarLabels: { format: string; keywords: string; structure: string };
}

const AnimatedScoreGraph: React.FC<AnimatedScoreGraphProps> = ({
  baseline,
  expectedMin,
  expectedMax,
  formatting,
  keywords,
  structure,
  pillarLabels,
}) => {
  const pathRef = useRef<SVGPathElement>(null);
  const bandRef = useRef<SVGPathElement>(null);
  const [pathLen, setPathLen] = useState(0);
  const [progress, setProgress] = useState(0);
  const [dotVisible, setDotVisible] = useState(false);

  // SVG viewport
  const W = 260;
  const H = 160;
  const PL = 38; // left padding (y-axis labels)
  const PR = 18;
  const PT = 14;
  const PB = 30; // bottom padding (x-axis labels)
  const CW = W - PL - PR;
  const CH = H - PT - PB;

  // Score → Y coordinate (higher score = higher on chart)
  const scoreY = (s: number) => PT + CH - ((Math.min(100, Math.max(0, s)) / 100) * CH);
  // X position (0=left/now, 1=right/after)
  const posX = (t: number) => PL + t * CW;

  // Smooth sigmoid curve: starts at baseline, rises to expectedMax
  const curve = (t: number) => {
    const sigmoid = 1 / (1 + Math.exp(-7 * (t - 0.45)));
    const norm = (sigmoid - (1 / (1 + Math.exp(7 * 0.45))));
    const scale = 1 / (1 - 2 * (1 / (1 + Math.exp(7 * 0.45))));
    const pct = Math.max(0, Math.min(1, norm * scale + 0.5));
    return baseline + (expectedMax - baseline) * pct;
  };

  // Generate main path + band paths
  const N = 60;
  const pts = Array.from({ length: N + 1 }, (_, i) => {
    const t = i / N;
    return { x: posX(t), y: scoreY(curve(t)) };
  });

  const mainPath = pts.map((p, i) => (i === 0 ? `M${p.x},${p.y}` : `L${p.x},${p.y}`)).join(' ');

  // Band: from expectedMin curve to expectedMax curve (shaded area between)
  const curveMin = (t: number) => baseline + (expectedMin - baseline) * (() => {
    const s = 1 / (1 + Math.exp(-7 * (t - 0.45)));
    const n = (s - (1 / (1 + Math.exp(7 * 0.45))));
    const sc = 1 / (1 - 2 * (1 / (1 + Math.exp(7 * 0.45))));
    return Math.max(0, Math.min(1, n * sc + 0.5));
  })();

  const bandPts = Array.from({ length: N + 1 }, (_, i) => ({ x: posX(i / N), y: scoreY(curve(i / N)) }));
  const bandPtsMin = Array.from({ length: N + 1 }, (_, i) => ({ x: posX(i / N), y: scoreY(curveMin(i / N)) }));
  const bandPath = [
    ...bandPts.map((p, i) => (i === 0 ? `M${p.x},${p.y}` : `L${p.x},${p.y}`)),
    ...bandPtsMin.reverse().map((p, i) => (i === 0 ? `L${p.x},${p.y}` : `L${p.x},${p.y}`)),
    'Z'
  ].join(' ');

  // Y-axis grid lines
  const gridScores = [0, 25, 50, 75, 100];

  // Measure path length for animation
  useEffect(() => {
    if (pathRef.current) {
      const len = pathRef.current.getTotalLength();
      setPathLen(len);
    }
  }, [mainPath]);

  // Animate on mount
  useEffect(() => {
    if (!pathLen) return;
    let start: number | null = null;
    const duration = 1600;
    const tick = (ts: number) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      // easeInOut
      const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
      setProgress(eased);
      if (p < 1) requestAnimationFrame(tick);
      else setDotVisible(true);
    };
    requestAnimationFrame(tick);
  }, [pathLen]);

  const endX = posX(1);
  const endY = scoreY(expectedMax);

  const metrics: { label: string; value: number }[] = [
    { label: pillarLabels.format, value: Math.round(formatting) },
    { label: pillarLabels.keywords, value: Math.round(keywords) },
    { label: pillarLabels.structure, value: Math.round(structure) },
  ];

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 pt-4">
        <TrendingUp className="w-4 h-4 text-[#6C47FF] dark:text-[#5B8DFF]" />
        <h4 className="font-semibold text-gray-900 dark:text-white text-sm">Score Trajectory</h4>
        <span className="ml-auto text-xs font-semibold text-green-600 dark:text-green-400">
          +{expectedMin - baseline}–{expectedMax - baseline}% expected
        </span>
      </div>

      {/* SVG Graph */}
      <div className="px-3">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          style={{ overflow: 'visible' }}
          aria-label="Score improvement graph"
        >
          {/* Grid lines */}
          {gridScores.map(s => (
            <g key={s}>
              <line
                x1={PL} y1={scoreY(s)} x2={PL + CW} y2={scoreY(s)}
                stroke="currentColor"
                strokeOpacity={0.08}
                strokeWidth={1}
                className="text-gray-500"
              />
              <text
                x={PL - 5} y={scoreY(s) + 4}
                textAnchor="end"
                fontSize={9}
                fill="currentColor"
                className="text-gray-400 dark:text-gray-500"
                opacity={0.7}
              >
                {s}
              </text>
            </g>
          ))}

          {/* Axes */}
          <line x1={PL} y1={PT} x2={PL} y2={PT + CH} stroke="currentColor" strokeOpacity={0.2} strokeWidth={1} className="text-gray-500" />
          <line x1={PL} y1={PT + CH} x2={PL + CW} y2={PT + CH} stroke="currentColor" strokeOpacity={0.2} strokeWidth={1} className="text-gray-500" />

          {/* X-axis labels */}
          <text x={PL} y={H - 6} textAnchor="middle" fontSize={9} fill="currentColor" className="text-gray-400" opacity={0.8}>Now</text>
          <text x={PL + CW} y={H - 6} textAnchor="middle" fontSize={9} fill="currentColor" className="text-gray-400" opacity={0.8}>After</text>

          {/* Baseline dot */}
          <circle cx={posX(0)} cy={scoreY(baseline)} r={4} fill="#6C47FF" opacity={0.7} />
          <text x={posX(0) + 7} y={scoreY(baseline) + 4} fontSize={9} fill="#6C47FF" fontWeight={600}>{baseline}%</text>

          {/* Shaded band (min→max range) */}
          <path
            ref={bandRef}
            d={bandPath}
            fill="#22c55e"
            fillOpacity={0.08}
            style={{
              clipPath: `inset(0 ${100 - progress * 100}% 0 0)`,
            }}
          />

          {/* Main curve — animated with dasharray */}
          <path
            ref={pathRef}
            d={mainPath}
            fill="none"
            stroke="url(#scoreGrad)"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={pathLen || 999}
            strokeDashoffset={pathLen ? pathLen * (1 - progress) : 999}
            style={{ transition: 'none' }}
          />

          {/* Gradient definition */}
          <defs>
            <linearGradient id="scoreGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#6C47FF" />
              <stop offset="100%" stopColor="#22c55e" />
            </linearGradient>
            <linearGradient id="bandGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#22c55e" stopOpacity={0.15} />
              <stop offset="100%" stopColor="#22c55e" stopOpacity={0.02} />
            </linearGradient>
          </defs>

          {/* Animated endpoint dot + label */}
          {dotVisible && (
            <g>
              <circle cx={endX} cy={endY} r={5} fill="#22c55e" />
              <circle cx={endX} cy={endY} r={9} fill="#22c55e" fillOpacity={0.2} />
              <text x={endX - 7} y={endY - 10} fontSize={9} fill="#22c55e" fontWeight={700}>{expectedMin}–{expectedMax}%</text>
            </g>
          )}

          {/* Tick marks on x-axis at 25%, 50%, 75% */}
          {[0.25, 0.5, 0.75].map(t => (
            <line key={t} x1={posX(t)} y1={PT + CH} x2={posX(t)} y2={PT + CH + 4} stroke="currentColor" strokeOpacity={0.2} strokeWidth={1} className="text-gray-500" />
          ))}
        </svg>
      </div>

      {/* ATS pillars — values match left column */}
      <div className="space-y-2 border-t border-gray-200 px-4 pb-4 pt-3 dark:border-gray-700">
        {metrics.map((m, i) => (
          <motion.div
            key={m.label}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: dotVisible ? 1 : 0, x: dotVisible ? 0 : 10 }}
            transition={{ delay: i * 0.12, duration: 0.3 }}
            className="flex items-center gap-2"
          >
            <CheckCircle className="h-4 w-4 shrink-0 text-[#6C47FF] dark:text-[#5B8DFF]" />
            <span className="flex-1 text-xs text-gray-600 dark:text-gray-400">{m.label}</span>
            <span className="text-xs font-bold text-[#6C47FF] dark:text-[#5B8DFF]">{m.value}%</span>
            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <motion.div
                className="h-full rounded-full bg-[#6C47FF] dark:bg-[#5B8DFF]"
                initial={{ width: 0 }}
                animate={{ width: dotVisible ? `${m.value}%` : 0 }}
                transition={{ delay: i * 0.15 + 0.3, duration: 0.5 }}
              />
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const BaselineAnalysisStep: React.FC<BaselineAnalysisStepProps> = ({
  onNext,
  onBack,
  resumeData,
  jobData,
  baselineScores,
  isLoading
}) => {
  const { t } = useTranslation();
  const isNative = Capacitor.isNativePlatform();
  const [showMismatchModal, setShowMismatchModal] = useState(false);
  const [mismatchResult, setMismatchResult] = useState<RoleMismatchResult | null>(null);
  const [acknowledgedMismatch, setAcknowledgedMismatch] = useState(false);

  // Calculate job match using ML algorithm
  const matchAnalysis = useMemo(() => {
    if (!jobData || !resumeData.parsedText) {
      return null;
    }

    const resumeSkills: string[] = [];
    if (resumeData.structured?.skills) {
      resumeSkills.push(...resumeData.structured.skills);
    }
    const skillPatterns = /skills?:?\s*([^\n]+)/gi;
    const matches = resumeData.parsedText.matchAll(skillPatterns);
    for (const match of matches) {
      const skills = match[1].split(/[,;|]/).map((s: string) => s.trim()).filter(Boolean);
      resumeSkills.push(...skills);
    }

    return analyzeJobMatch(
      {
        skills: [...new Set(resumeSkills)],
        experience: resumeData.parsedText,
        education: resumeData.parsedText,
        keywords: resumeData.parsedText.toLowerCase().split(/\s+/)
      },
      {
        requiredSkills: jobData.required_skills || [],
        preferredSkills: jobData.preferred_skills || [],
        requirements: jobData.requirements || [],
        description: jobData.description || '',
        experienceLevel: jobData.experience_level
      }
    );
  }, [resumeData, jobData]);

  const optimizationGuidance = useMemo(() => {
    if (!matchAnalysis) return { proceed: true, showWarning: false, warningMessage: '' };
    return getOptimizationGuidance(matchAnalysis);
  }, [matchAnalysis]);

  // Detect role mismatch using LLM (async)
  // DISABLED: Consuming too many tokens, causing rate limits
  useEffect(() => {
    let isCancelled = false;
    
    const analyzeRoleMismatch = async () => {
      if (!jobData || !resumeData.parsedText || acknowledgedMismatch) {
        return;
      }

      // Skip role mismatch detection to avoid rate limits
      // User can still proceed with optimization
      console.log('[BaselineAnalysis] Role mismatch detection skipped to conserve API tokens');
      return;

      /* TEMPORARILY DISABLED DUE TO RATE LIMITS
      try {
        console.log('[BaselineAnalysis] Starting role mismatch detection...');
        
        const result = await detectRoleMismatch(
          resumeData.parsedText,
          jobData.title || '',
          jobData.description || '',
          jobData.required_skills || []
        );
        
        if (!isCancelled && result.isSevereMismatch && !showMismatchModal) {
          console.log('[BaselineAnalysis] Severe mismatch detected, showing modal');
          setMismatchResult(result);
          setShowMismatchModal(true);
        }
      } catch (error) {
        console.error('[BaselineAnalysis] Role mismatch detection failed:', error);
        // Continue without blocking if detection fails
      }
      */
    };

    analyzeRoleMismatch();
    
    return () => {
      isCancelled = true;
    };
  }, [resumeData.parsedText, jobData, acknowledgedMismatch, showMismatchModal]);

  const getFitLevelColor = (fitLevel: string) => {
    switch (fitLevel) {
      case 'excellent': 
      case 'good': 
        return { 
          color: 'var(--vscode-success)', 
          bg: 'var(--vscode-success-bg)', 
          border: 'var(--vscode-success-border)' 
        };
      case 'moderate': 
        return { 
          color: 'var(--vscode-info)', 
          bg: 'var(--vscode-info-bg)', 
          border: 'var(--vscode-info-border)' 
        };
      case 'low': 
      case 'poor': 
        return { 
          color: 'var(--vscode-warning)', 
          bg: 'var(--vscode-warning-bg)', 
          border: 'var(--vscode-warning-border)' 
        };
      default: 
        return { 
          color: 'var(--vscode-text-tertiary)', 
          bg: 'var(--vscode-bg-tertiary)', 
          border: 'var(--vscode-border-primary)' 
        };
    }
  };

  /* ─────────────────────────────────────────────────────────────────────────
     NATIVE MOBILE RENDER — rich analytics splash screen
     ───────────────────────────────────────────────────────────────────────── */
  if (isNative) {
    const atsScore = baselineScores.overall;
    const scoreColor = atsScore >= 70 ? '#22c55e' : atsScore >= 45 ? '#6C47FF' : '#f59e0b';
    const scoreLabel = atsScore >= 70 ? 'Good' : atsScore >= 45 ? 'Fair' : 'Needs Work';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.3 }}
        className=""
        style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', isolation: 'isolate' as const }}
      >
        {/* ── Gradient header ── */}
        <div
          className="flex-shrink-0"
          style={{
            background: 'var(--ggj-header-bg)',
            borderRadius: '0 0 24px 24px',
            boxShadow: 'var(--brand-cta-shadow, 0 4px 20px rgba(108,71,255,0.30))',
            paddingTop: 'max(calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 8px), 40px)',
          }}
        >
          <div className="flex items-center justify-between px-4 pb-4">
            <button type="button" onClick={onBack} className="w-9 h-9 flex items-center justify-center rounded-xl touch-manipulation" style={{ background: 'var(--ggj-header-btn-bg)', color: 'var(--ggj-header-btn-icon)' }}>
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex flex-col items-center">
              <span className="text-[10px] font-semibold uppercase tracking-widest leading-none" style={{ color: 'var(--ggj-header-sub)' }}>Resume Optimizer</span>
              <span className="text-base font-bold leading-tight mt-0.5" style={{ color: 'var(--ggj-header-text)' }}>Job Match Analysis</span>
            </div>
            <div className="w-9 h-9" />
          </div>

          {/* ── Big ATS score hero inside header ── */}
          <div className="flex items-center justify-between px-5 pb-5">
            {/* Score ring */}
            <div className="flex items-center gap-4">
              <div className="relative w-20 h-20 flex-shrink-0">
                <svg viewBox="0 0 80 80" className="w-20 h-20 -rotate-90">
                  <circle cx="40" cy="40" r="34" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="7" />
                  <circle
                    cx="40" cy="40" r="34" fill="none"
                    stroke="white" strokeWidth="7"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 34}`}
                    strokeDashoffset={`${2 * Math.PI * 34 * (1 - atsScore / 100)}`}
                    style={{ transition: 'stroke-dashoffset 1s ease' }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-xl font-extrabold text-white leading-none">{atsScore}%</span>
                  <span className="text-[9px] text-white/70 leading-none mt-0.5">ATS</span>
                </div>
              </div>
              <div>
                <div className="text-white font-bold text-base leading-tight">Current Score</div>
                <div className="text-purple-200 text-xs mt-0.5">Baseline before optimization</div>
                <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: scoreColor }} />
                  <span className="text-xs font-semibold text-white">{scoreLabel}</span>
                </div>
              </div>
            </div>
            {/* Expected range */}
            <div className="text-right">
              <div className="text-xs text-purple-200 mb-0.5">After optimization</div>
              <div className="text-2xl font-extrabold text-white">
                {Math.min(100, atsScore + 25)}–{Math.min(100, atsScore + 35)}%
              </div>
              <div className="text-xs text-green-300 font-semibold">+{25}–{35}% expected</div>
            </div>
          </div>
        </div>

        {/* ── Scrollable content ── */}
        <div className="flex-1 min-h-0 overflow-y-auto" style={{ background: 'var(--ggj-bg-page)' }}>
          <div className="px-4 pt-4 pb-4 flex flex-col gap-4">

            {/* Score breakdown row */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Format', value: baselineScores.formatting },
                { label: 'Keywords', value: baselineScores.keywords },
                { label: 'Structure', value: baselineScores.structure },
              ].map(({ label, value }) => (
                <div key={label} className="rounded-2xl p-3 flex flex-col items-center gap-1.5" style={{ background: 'var(--ggj-bg-card)', border: '1px solid var(--ggj-border)', boxShadow: 'var(--ggj-shadow-card)' }}>
                  <div className="relative w-12 h-12">
                    <svg viewBox="0 0 48 48" className="w-12 h-12 -rotate-90">
                      <circle cx="24" cy="24" r="19" fill="none" stroke="#e5e7eb" strokeWidth="4.5" />
                      <circle
                        cx="24" cy="24" r="19" fill="none"
                        stroke="#6C47FF" strokeWidth="4.5"
                        strokeLinecap="round"
                        strokeDasharray={`${2 * Math.PI * 19}`}
                        strokeDashoffset={`${2 * Math.PI * 19 * (1 - value / 100)}`}
                        style={{ transition: 'stroke-dashoffset 1s ease' }}
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xs font-bold text-[#6C47FF]">{value}%</span>
                    </div>
                  </div>
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{label}</span>
                </div>
              ))}
            </div>

            {/* Job info card */}
            {jobData && (
              <div className="rounded-2xl p-4" style={{ background: 'var(--ggj-bg-card)', border: '1px solid var(--ggj-border)', boxShadow: 'var(--ggj-shadow-card)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-4 h-4 text-[#6C47FF]" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Applying for</div>
                    <div className="text-sm font-bold text-gray-900 dark:text-white leading-tight">{jobData.title}</div>
                  </div>
                  {jobData.company && (
                    <div className="ml-auto flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                      <Building className="w-3.5 h-3.5 shrink-0" />
                      {jobData.company}
                    </div>
                  )}
                </div>
                {jobData.required_skills && jobData.required_skills.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {jobData.required_skills.slice(0, 6).map((skill: string, idx: number) => (
                      <span key={idx} className="px-2 py-0.5 text-xs rounded-lg bg-purple-50 dark:bg-purple-900/30 text-[#6C47FF] dark:text-purple-300 font-medium">{skill}</span>
                    ))}
                    {jobData.required_skills.length > 6 && (
                      <span className="px-2 py-0.5 text-xs rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500">+{jobData.required_skills.length - 6}</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Optional narrative tip (no second % — scores above are the source of truth) */}
            {matchAnalysis?.recommendation?.trim() && (
              <div
                className="flex gap-3 rounded-2xl border p-4"
                style={{
                  background: 'var(--ggj-bg-card)',
                  borderColor: 'var(--ggj-border)',
                  boxShadow: 'var(--ggj-shadow-card)',
                }}
              >
                <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#6C47FF]" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('optimizer.baselineStep.optimizationFocus', 'Optimization focus')}
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
                    {matchAnalysis.recommendation}
                  </p>
                </div>
              </div>
            )}

            {/* Score trajectory graph */}
            <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--ggj-bg-card)', border: '1px solid var(--ggj-border)', boxShadow: 'var(--ggj-shadow-card)' }}>
              <div className="flex items-center justify-between px-4 pb-0 pt-3">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-[#6C47FF]" />
                  <span className="text-sm font-bold" style={{ color: 'var(--ggj-text-heading)' }}>Score Trajectory</span>
                </div>
                <span className="text-xs font-bold text-green-600 dark:text-green-400">+{25}–{35}% expected</span>
              </div>
              <AnimatedScoreGraph
                baseline={atsScore}
                expectedMin={Math.min(100, atsScore + 25)}
                expectedMax={Math.min(100, atsScore + 35)}
                formatting={baselineScores.formatting}
                keywords={baselineScores.keywords}
                structure={baselineScores.structure}
                pillarLabels={{
                  format: t('optimizer.baselineStep.format'),
                  keywords: t('optimizer.baselineStep.keywords'),
                  structure: t('optimizer.baselineStep.structure'),
                }}
              />
            </div>

            {/* Resume stats */}
            <div className="rounded-2xl p-4" style={{ background: 'var(--ggj-bg-card)', border: '1px solid var(--ggj-border)', boxShadow: 'var(--ggj-shadow-card)' }}>
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-green-600 dark:text-green-400" />
                <span className="text-sm font-bold" style={{ color: 'var(--ggj-text-heading)' }}>Your Resume</span>
                <CheckCircle className="w-4 h-4 text-green-500 ml-auto" />
                <span className="text-xs text-green-600 dark:text-green-400 font-semibold">Ready</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl p-3 text-center" style={{ background: 'var(--ggj-bg-card-inner)' }}>
                  <div className="text-lg font-extrabold text-gray-900 dark:text-white">{resumeData.metadata?.wordCount || resumeData.parsedText?.split(/\s+/).length || 0}</div>
                  <div className="text-xs" style={{ color: 'var(--ggj-text-muted)' }}>Words</div>
                </div>
                <div className="rounded-xl p-3 text-center" style={{ background: 'var(--ggj-bg-card-inner)' }}>
                  <div className="text-lg font-extrabold text-gray-900 dark:text-white">{resumeData.metadata?.pageCount || 1}</div>
                  <div className="text-xs" style={{ color: 'var(--ggj-text-muted)' }}>
                    {(resumeData.metadata?.pageCount || 1) === 1
                      ? t('optimizer.baselineStep.pageSingular')
                      : t('optimizer.baselineStep.pagePlural')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Fixed bottom CTA (always on top of scroll) ── */}
        <div
          className="flex-shrink-0 px-4 pt-3 border-t"
          style={{
            position: 'relative' as const,
            zIndex: 200,
            background: 'var(--ggj-nav-bg)',
            borderColor: 'var(--ggj-border)',
            paddingBottom: 'max(12px, env(safe-area-inset-bottom, 0px))',
          }}
        >
          <button
            type="button"
            onClick={onNext}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-sm font-bold text-white disabled:opacity-50 touch-manipulation"
            style={{ background: 'var(--ggj-btn-bg)', border: '1px solid var(--ggj-btn-border)', color: 'var(--ggj-btn-text)', boxShadow: 'var(--ggj-btn-shadow)' }}
          >
            <Settings className="w-4 h-4 shrink-0" />
            Continue to Settings
            <ArrowRight className="w-4 h-4 shrink-0" />
          </button>
        </div>
      </motion.div>
    );
  }

  /* ─────────────────────────────────────────────────────────────────────────
     WEB RENDER (unchanged)
     ───────────────────────────────────────────────────────────────────────── */
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col min-h-0 flex-1"
    >
      <div className="w-full min-w-0 max-w-full lg:max-w-5xl lg:mx-auto px-3 xs:px-4 sm:px-5 lg:px-8 flex flex-col gap-0 pb-4 py-2 sm:py-3">
        <div className="w-full">
          <div className={STEP_CARD_COMPACT_CLASS}>
          {/* Role Mismatch Modal - BLOCKS OPTIMIZATION */}
      {showMismatchModal && mismatchResult && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4 pb-[env(safe-area-inset-bottom)]">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="vscode-bg-secondary border vscode-border rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
          >
            <div className="relative p-4 sm:p-8 overflow-y-auto max-h-[90vh] overscroll-contain">
              {/* Icon */}
              <div className="flex justify-center -mt-12 mb-6">
                <div className="vscode-bg-secondary rounded-full p-2">
                  <div className="rounded-full p-3" style={{ backgroundColor: 'var(--vscode-warning-bg)' }}>
                    <AlertTriangle className="w-10 h-10" style={{ color: 'var(--vscode-warning)' }} />
                  </div>
                </div>
              </div>

              {/* Header */}
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold vscode-text-primary mb-3">
                  Severe Career Path Mismatch Detected
                </h2>
                <p className="vscode-text-secondary text-base leading-relaxed">
                  {mismatchResult.warning?.message}
                </p>
              </div>

              {/* Domain Comparison Card */}
              <div className="vscode-bg-primary/50 border vscode-border rounded-xl p-4 sm:p-6 mb-6">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center mb-4">
                  <div className="text-center">
                    <div className="text-xs vscode-text-tertiary mb-2 uppercase tracking-wide">{t('optimizer.baselineStep.yourResumeLabel')}</div>
                    <div className="vscode-bg-secondary border vscode-border rounded-lg p-4">
                      <div className="text-xl font-bold uppercase" style={{ color: 'var(--vscode-primary)' }}>
                        {mismatchResult.resumeDomain}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-center">
                    <div className="w-14 h-14 border-2 rounded-full flex items-center justify-center" style={{
                      backgroundColor: 'var(--vscode-error-bg)',
                      borderColor: 'var(--vscode-error-border)'
                    }}>
                      <XCircle className="w-7 h-7" style={{ color: 'var(--vscode-error)' }} />
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-xs vscode-text-tertiary mb-2 uppercase tracking-wide">Job Requirement</div>
                    <div className="vscode-bg-secondary border vscode-border rounded-lg p-4">
                      <div className="text-xl font-bold uppercase" style={{ color: 'var(--vscode-primary)' }}>
                        {mismatchResult.jobDomain}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="text-center pt-4 border-t vscode-border">
                  <span className="font-semibold text-base" style={{ color: 'var(--vscode-error)' }}>
                    {t('optimizer.baselineStep.incompatibilityScore')}: {mismatchResult.mismatchScore}%
                  </span>
                </div>
              </div>

              {/* Risks Section */}
              <div className="vscode-bg-primary/50 border vscode-border rounded-xl p-6 mb-6">
                <h3 className="vscode-text-primary font-semibold text-lg mb-4 flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2" style={{ color: 'var(--vscode-warning)' }} />
                  {t('optimizer.baselineStep.potentialConsequences')}
                </h3>
                <div className="space-y-2">
                  {mismatchResult.warning?.risks.map((risk, idx) => (
                    <div key={idx} className="flex items-start space-x-2 vscode-text-secondary text-sm">
                      <span className="mt-1" style={{ color: 'var(--vscode-primary)' }}>•</span>
                      <span>{risk}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Professional Disclaimer */}
              <div className="rounded-xl p-5 mb-6" style={{
                backgroundColor: 'var(--vscode-info-bg)',
                borderColor: 'var(--vscode-info-border)',
                borderWidth: '1px'
              }}>
                <div className="flex items-start space-x-3">
                  <Info className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--vscode-info)' }} />
                  <div className="flex-1">
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--vscode-info)' }}>Professional Recommendation</h4>
                    <p className="vscode-text-secondary text-sm leading-relaxed">
                      {mismatchResult.warning?.disclaimer}
                    </p>
                  </div>
                </div>
              </div>

              {/* Acknowledgment Checkbox */}
              <label className="flex items-start space-x-3 cursor-pointer vscode-bg-primary/50 border vscode-border rounded-xl p-5 hover:vscode-border-accent transition-colors mb-6">
                <input
                  type="checkbox"
                  checked={acknowledgedMismatch}
                  onChange={(e) => setAcknowledgedMismatch(e.target.checked)}
                  className="w-5 h-5 mt-0.5 rounded vscode-border text-cyan-500 focus:ring-2 focus:ring-cyan-500/50 vscode-bg-primary"
                />
                <div className="flex-1">
                  <span className="vscode-text-primary font-medium block mb-1">
                    {t('optimizer.baselineStep.acknowledgeMismatch')}
                  </span>
                  <span className="vscode-text-tertiary text-sm">
                    {t('optimizer.baselineStep.acknowledgeDisclaimer')}
                  </span>
                </div>
              </label>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={onBack}
                  className="flex-1 px-6 py-3 font-semibold rounded-lg transition-colors"
                  style={{
                    backgroundColor: 'var(--vscode-primary)',
                    color: 'var(--vscode-primary-text)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--vscode-primary-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--vscode-primary)';
                  }}
                >
                  {t('optimizer.baselineStep.goBackChangeJob')}
                </button>
                
                <button
                  onClick={() => {
                    setShowMismatchModal(false);
                  }}
                  disabled={!acknowledgedMismatch}
                  className="flex-1 px-6 py-3 font-semibold rounded-lg transition-colors"
                  style={
                    acknowledgedMismatch
                      ? {
                          backgroundColor: 'var(--vscode-bg-tertiary)',
                          color: 'var(--vscode-text-secondary)'
                        }
                      : {
                          backgroundColor: 'var(--vscode-bg-secondary)',
                          color: 'var(--vscode-text-tertiary)',
                          cursor: 'not-allowed'
                        }
                  }
                >
                  {t('optimizer.baselineStep.proceedAnyway')}
                </button>
              </div>

              {/* Bottom hint */}
              <div className="mt-6 text-center">
                <p className="vscode-text-tertiary text-sm">
                  {t('optimizer.baselineStep.recommendFindingJob')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

          {/* Header */}
          <div className={STEP_INNER_HEADER_CLASS}>
            <div className="flex items-start gap-3">
              <div className={STEP_HEADER_ICON_CLASS}>
                <Target className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="mb-0.5 text-lg font-bold text-gray-900 dark:text-white">{t('optimizer.baselineStep.title')}</h2>
                <p className="line-clamp-2 text-xs text-gray-600 dark:text-gray-400 sm:line-clamp-none">
                  {t('optimizer.baselineStep.subtitle')}
                </p>
              </div>
            </div>
          </div>

          {/* Two Column Layout - Job/Resume vs AI Analysis */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 min-w-0 w-full">
        
            {/* LEFT: Job Requirements & Your Resume */}
            <div className="border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700">
              <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#6C47FF]/10 dark:bg-[#5B3FFF]/20">
                    <Target className="w-4 h-4 text-[#6C47FF] dark:text-[#5B8DFF]" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">What You're Applying For</h3>
                    <p className="text-xs" style={{ color: 'var(--ggj-text-muted)' }}>Job vs your profile</p>
                  </div>
                </div>
              </div>
              <div className="p-3 sm:p-4 space-y-3">
                {/* Job Requirements */}
                {jobData && (
                  <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Briefcase className="w-4 h-4 text-[#6C47FF] dark:text-[#5B8DFF]" />
                      <h4 className="font-semibold text-gray-900 dark:text-white text-sm">{t('optimizer.baselineStep.jobRequirements')}</h4>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="font-medium text-gray-900 dark:text-white">{jobData.title}</div>
                      <div className="text-gray-600 dark:text-gray-400">{jobData.company}</div>
                      {jobData.location && (
                        <div className="flex items-center gap-1 text-gray-500 dark:text-gray-500 text-xs">
                          <MapPin className="w-3 h-3" />
                          {jobData.location}
                        </div>
                      )}
                      {jobData.required_skills && jobData.required_skills.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {jobData.required_skills.slice(0, 8).map((skill: string, idx: number) => (
                            <span
                              key={idx}
                              className="px-2 py-0.5 text-xs rounded-lg bg-[#6C47FF]/10 dark:bg-[#5B3FFF]/20 text-[#6C47FF] dark:text-[#5B8DFF]"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Your Resume */}
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <h4 className="font-semibold text-gray-900 dark:text-white text-sm">{t('optimizer.baselineStep.yourResume')}</h4>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800/60">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {resumeData.metadata?.wordCount || resumeData.parsedText?.split(/\s+/).length || 0}
                      </div>
                      <div className="text-xs" style={{ color: 'var(--ggj-text-muted)' }}>{t('optimizer.baselineStep.words')}</div>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800/60">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {resumeData.metadata?.pageCount || 1}
                      </div>
                      <div className="text-xs" style={{ color: 'var(--ggj-text-muted)' }}>
                        {(resumeData.metadata?.pageCount || 1) === 1
                          ? t('optimizer.baselineStep.pageSingular')
                          : t('optimizer.baselineStep.pagePlural')}
                      </div>
                    </div>
                    <div className="flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800/60">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <div className="text-xs" style={{ color: 'var(--ggj-text-muted)' }}>{t('optimizer.baselineStep.ready')}</div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{t('optimizer.baselineStep.skillsExtractedNote')}</p>
                </div>

                {/* Current Baseline Scores - left column */}
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-3 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-[#6C47FF] dark:text-[#5B8DFF]" />
                    {t('optimizer.baselineStep.currentBaselineScores')}
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between items-center mb-1 text-sm">
                        <span className="text-gray-600 dark:text-gray-400">{t('optimizer.baselineStep.overallAtsScore')}</span>
                        <span className="font-semibold text-[#6C47FF] dark:text-[#5B8DFF]">{baselineScores.overall}%</span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                        <div
                          className="h-2 rounded-full bg-indigo-600 transition-all duration-500 dark:bg-[#5B8DFF]"
                          style={{ width: `${baselineScores.overall}%` }}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800/60">
                        <div className="text-sm font-bold text-[#6C47FF] dark:text-[#5B8DFF]">{baselineScores.formatting}%</div>
                        <div className="text-xs" style={{ color: 'var(--ggj-text-muted)' }}>{t('optimizer.baselineStep.format')}</div>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800/60">
                        <div className="text-sm font-bold text-[#6C47FF] dark:text-[#5B8DFF]">{baselineScores.keywords}%</div>
                        <div className="text-xs" style={{ color: 'var(--ggj-text-muted)' }}>{t('optimizer.baselineStep.keywords')}</div>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800/60">
                        <div className="text-sm font-bold text-[#6C47FF] dark:text-[#5B8DFF]">{baselineScores.structure}%</div>
                        <div className="text-xs" style={{ color: 'var(--ggj-text-muted)' }}>{t('optimizer.baselineStep.structure')}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: trajectory + same ATS pillars as left (single source of truth) */}
            <div className="border-t border-gray-200 dark:border-gray-700 lg:border-t-0 lg:border-l">
              <div className="border-b border-gray-200 p-3 dark:border-gray-700 sm:p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#6C47FF]/10 dark:bg-[#5B3FFF]/20">
                    <BarChart3 className="h-4 w-4 text-[#6C47FF] dark:text-[#5B8DFF]" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('optimizer.baselineStep.aiAnalysisPredictions')}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('optimizer.baselineStep.trajectoryAtsSubtitle')}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3 p-3 sm:p-4">
                <div className="mb-3 overflow-hidden rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/50">
                  <AnimatedScoreGraph
                    baseline={baselineScores.overall}
                    expectedMin={Math.min(100, baselineScores.overall + 25)}
                    expectedMax={Math.min(100, baselineScores.overall + 35)}
                    formatting={baselineScores.formatting}
                    keywords={baselineScores.keywords}
                    structure={baselineScores.structure}
                    pillarLabels={{
                      format: t('optimizer.baselineStep.format'),
                      keywords: t('optimizer.baselineStep.keywords'),
                      structure: t('optimizer.baselineStep.structure'),
                    }}
                  />
                </div>

                {matchAnalysis?.recommendation?.trim() && (
                  <div className="flex gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/50">
                    <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#6C47FF] dark:text-[#5B8DFF]" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                        {t('optimizer.baselineStep.optimizationFocus', 'Optimization focus')}
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-gray-600 dark:text-gray-400">
                        {matchAnalysis.recommendation}
                      </p>
                    </div>
                  </div>
                )}

                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={onNext}
                    disabled={isLoading}
                    aria-label={t('optimizer.baselineStep.optimizationSettings')}
                    className="inline-flex min-h-[44px] touch-manipulation items-center justify-center gap-2 rounded-xl border border-indigo-500/30 bg-[#6C47FF] px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-[#5B8DFF] dark:hover:opacity-95"
                  >
                    <Settings className="h-4 w-4 shrink-0 text-white" />
                    <span className="text-white">{t('optimizer.baselineStep.optimizationSettings')}</span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-white" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          </div>

        <nav className={STEP_NAV_CLASS} aria-label="Step navigation">
          <button
            type="button"
            onClick={onBack}
            className="flex min-h-[44px] min-w-0 flex-initial touch-manipulation items-center justify-center gap-2 rounded-lg px-3 py-3 font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-white sm:px-4"
          >
            <ArrowLeft className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline">{t('optimizer.baselineStep.backShort')}</span>
          </button>
        </nav>
        </div>
      </div>
    </motion.div>
  );
};

export default React.memo(BaselineAnalysisStep);

